// File: src/components/AddClassificationDialog.jsx
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from '@mui/material';
import Select from 'react-select';

export default function AddClassificationDialog({ open, onClose, trainingSetId, domainId, onAdd }) {
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // When the dialog opens, fetch the entire list of DocumentClassifications for the given domain.
    useEffect(() => {
        if (!open) return;
        // Clear any previous error
        setErrorMessage('');
        async function fetchOptions() {
            setLoading(true);
            try {
                const response = await fetch(
                    `/api/DocumentClassifications?domainId=${domainId}`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                    }
                );
                if (response.ok) {
                    const data = await response.json();
                    // Assuming the data comes in as an array of objects like { value, label }
                    setOptions(data);
                } else {
                    setOptions([]);
                }
            } catch (error) {
                console.error('Error fetching classifications:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }
        fetchOptions();
    }, [open, domainId]);

    const handleConfirm = async () => {
        if (!selectedOption) return;
        try {
            const payload = {
                documentClassificationId: selectedOption.value,
            };
            const response = await fetch(`/api/TrainingSet/${trainingSetId}/classifications`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            });
            if (response.ok) {
                const newClassification = await response.json();
                onAdd(newClassification);
                onClose();
                setSelectedOption(null);
            } else if (response.status === 409) {
                // 409 indicates a duplicate record.
                setErrorMessage("This classification is already added to the training set.");
            } else {
                setErrorMessage("Failed to add classification. Please try again.");
            }
        } catch (error) {
            console.error('Error adding classification:', error);
            setErrorMessage("Error adding classification. Please try again.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add Classification</DialogTitle>
            <DialogContent>
                <Select
                    options={options}
                    value={selectedOption}
                    onChange={(option) => {
                        setSelectedOption(option);
                        setErrorMessage(''); // clear error when a new selection is made
                    }}
                    placeholder="Select classification..."
                    menuPortalTarget={document.body}
                    styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                />
                {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" color="primary" disabled={!selectedOption}>
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
}
